import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import StarIcon from '@mui/icons-material/Star'
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { DefaultCampaignChip } from 'src/components/Chips/DefaultCampaignChip'
import ModalBlockEditCampaing from 'src/components/Modal/ModalBlockEditCampaing'
import { GridActions } from 'src/components/Table/GridActions'
import { Table } from 'src/components/Table/Table'
import { ArrToNumberSort } from 'src/components/Table/SortingFunctions'
import { getActionIconSx, toolbarButtonSx, toolbarIconSx } from 'src/components/Table/styles'
import { isPastDate } from 'src/components/Table/utils'
import { StyledTooltip } from 'src/components/Tooltip/StyledTooltip'
import { AlertContext } from 'src/context/AlertContext'
import { ROUTES } from 'src/routes/Router'
import { goToCampaignResult, goToEditCampaign } from 'src/routes/coordinator'
import { getPermissions } from 'src/service/authService'
import {
  deleteCampaign,
  disableDefaultCampaigns,
  editCampaign,
  getCampaigns,
  recycleCampaignById,
  unarchiveCampaignById,
} from 'src/service/marketingCampaignService'
import { formatDateAndTime } from 'src/util/formatDataAndTime'
import CreateCampaign from '../../CreateCampaign'

export default function CampaignTable() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [tableKey, setTableKey] = useState(0)
  const [showBlockModal, setShowBlockModal] = useState(false)
  const { createCRUDAlert, createAlert } = useContext(AlertContext)
  const permissions = JSON.parse(getPermissions())

  const getData = async (options: any) => {
    const data = await getCampaigns(options)
    return {
      data: data.listMarketingCampaign,
      totalPages: data.page,
      size: data?.size,
    }
  }

  const handleDelete = async (id: string, name: string) => {
    const res = await deleteCampaign(id)
    if (res) createCRUDAlert(ROUTES.CAMPAIGNS, name, 'deleted')
  }

  const handleRecycle = async (id: string, name: string) => {
    const res = await recycleCampaignById(id)
    if (res) {
      createCRUDAlert(ROUTES.CAMPAIGNS, name, 'edited')
    }
  }

  const handleUnarchive = async (id: string, name: string) => {
    const res = await unarchiveCampaignById(id)
    if (res) {
      createCRUDAlert(ROUTES.CAMPAIGNS, name, 'edited')
    }
  }

  // --- Campaign-specific: default campaign logic ---
  const handleMakeDefaultCampaign = async (
    row: any,
    value: boolean,
    refreshData: () => void
  ) => {
    try {
      if (value) {
        await disableDefaultCampaigns(row?.companyId)
      }
      await editCampaign(row?.id, {
        ...row,
        defaultCampaign: value,
      })
      refreshData()
      if (value) {
        const bannerMessage = `${t(
          'table.campaign.default.banner.prefix'
        )} "${row?.name}" ${t('table.campaign.default.banner.suffix')}`
        createAlert(ROUTES.CAMPAIGNS, bannerMessage)
      }
    } catch (error) {
      console.error('[CampaignTable] Error updating default campaign:', error)
    }
  }

  // --- Campaign-specific: extra row actions ---
  const renderExtraRowActions = (
    row: any,
    isHovered: boolean,
    refreshData: () => void
  ) => {
    const actions: React.ReactNode[] = []

    // Results button
    actions.push(
      <GridActions
        key="results"
        sx={{
          display: isHovered ? 'flex' : 'none',
          transition: 'opacity 0.3s ease-in-out',
          color: '#00000099',
          ':hover': {
            color: '#ffff',
            backgroundColor: '#00000099',
            borderRadius: '4px',
          },
        }}
        icon={
          <Tooltip title={t('table.action.result.tip')}>
            <BarChartRoundedIcon />
          </Tooltip>
        }
        onClick={() => goToCampaignResult(navigate, row.id)}
        label="Results"
        color="inherit"
      />
    )

    // Make Default star (non-default, non-quiz campaigns)
    if (!row?.defaultCampaign && row?.contentType !== 'quiz') {
      actions.push(
        <GridActions
          key="make-default"
          sx={{
            ...getActionIconSx(isHovered),
            display: isHovered ? 'block' : 'none',
          }}
          icon={
            <Tooltip title={t('button.default.campaigin.table.action.tip')}>
              <StarIcon />
            </Tooltip>
          }
          onClick={() => handleMakeDefaultCampaign(row, true, refreshData)}
          color="inherit"
        />
      )
    }

    // Remove Default star (default campaigns)
    if (row?.defaultCampaign) {
      actions.push(
        <GridActions
          key="remove-default"
          sx={{
            opacity: isHovered ? 1 : 0,
            display: isHovered ? 'block' : 'none',
            transition: 'opacity 0.3s ease-in-out',
            color: '#00000099',
            ':hover': {
              color: '#ffff',
              backgroundColor: '#FF5E1E',
              borderRadius: '4px',
              '#defaultStartIcon': {
                color: 'white',
              },
            },
          }}
          icon={
            <StyledTooltip content={t('campaign.default.remove.tip')}>
              <StarIcon
                id="defaultStartIcon"
                sx={{
                  color: '#FF5E1E',
                }}
              />
            </StyledTooltip>
          }
          onClick={() => handleMakeDefaultCampaign(row, false, refreshData)}
          color="inherit"
        />
      )
    }

    return actions
  }

  // --- Campaign-specific: extra toolbar actions ---
  const renderExtraToolbarActions = ({
    selectionModel,
    rows,
    refreshData,
  }: {
    selectionModel: any[]
    rows: any[]
    refreshData: () => void
  }) => {
    if (selectionModel.length > 1) return null

    const selectedCampaign = rows.find((r) => r.id === selectionModel[0])
    if (!selectedCampaign) return null

    const showResults = isPastDate(selectedCampaign?.initialDate)
    const canMakeDefault =
      !selectedCampaign.defaultCampaign &&
      selectedCampaign.contentType !== 'quiz'
    const isDefault = selectedCampaign.defaultCampaign

    return (
      <>
        {/* Results */}
        {showResults && (
          <Button
            onClick={() => goToCampaignResult(navigate, selectionModel[0])}
            sx={toolbarButtonSx}
          >
            <BarChartRoundedIcon sx={toolbarIconSx} />
            {t('table.button.cta.result')}
          </Button>
        )}

        {/* Make Default */}
        {canMakeDefault && (
          <Button
            onClick={() =>
              handleMakeDefaultCampaign(selectedCampaign, true, refreshData)
            }
            sx={toolbarButtonSx}
          >
            <StarIcon sx={toolbarIconSx} />
            {t('button.default.campaigin.table.action.tip')}
          </Button>
        )}

        {/* Remove Default */}
        {isDefault && (
          <Button
            onClick={() =>
              handleMakeDefaultCampaign(selectedCampaign, false, refreshData)
            }
            sx={{
              display: 'flex',
              ...toolbarButtonSx,
            }}
          >
            <StarIcon sx={{ ...toolbarIconSx, color: '#FF5E1E' }} />
            {t('campaign.default.remove.tip')}
          </Button>
        )}
      </>
    )
  }

  // --- Campaign-specific: block quiz editing ---
  const handleBeforeEdit = (row: any): boolean => {
    if (row?.contentType === 'quiz' && isPastDate(row?.initialDate)) {
      setShowBlockModal(true)
      return false
    }
    return true
  }

  const columns: GridColumns = [
    {
      field: 'name',
      headerName: t('campaign.table.header.campaign'),
      width: 250,
      editable: false,
      renderCell: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', gap: '20px' }}>
            {row?.name?.slice(0, 18)}
          </Box>
        )
      },
    },
    {
      field: 'contentType',
      headerName: t('campaign.table.header.type'),
      width: 90,
      editable: false,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (a) => t(`table.campanha.${a.value}`),
    },
    {
      field: 'isActive',
      headerName: t('table.header.situation'),
      type: 'boolean',
      editable: false,
      renderCell: ({ row }) => {
        if (row?.defaultCampaign)
          return (
            <Chip
              sx={{ background: '#FFA7261F' }}
              size="small"
              label={t('table.status.running')}
            />
          )
        if (new Date(row.initialDate) > new Date()) {
          if (row.isActive) {
            return (
              <Chip
                sx={{ background: '#FF5E1E1F' }}
                size="small"
                label={t('table.status.active')}
              />
            )
          } else {
            return (
              <Chip
                sx={{ background: '#00000014' }}
                size="small"
                label={t('table.status.inactive')}
              />
            )
          }
        }
        if (new Date() > new Date(row.finalDate)) {
          return (
            <Chip
              sx={{ background: '#2E7D321F' }}
              size="small"
              label={t('table.status.finished')}
            />
          )
        }
        if (
          new Date() < new Date(row.finalDate) &&
          new Date() > new Date(row.initialDate)
        ) {
          return (
            <Chip
              sx={{ background: '#FFA7261F' }}
              size="small"
              label={t('table.status.running')}
            />
          )
        }
      },
      width: 150,
    },
    {
      field: 'initialDate',
      headerName: t('campaign.table.header.duration'),
      type: 'dateTime',
      editable: false,
      headerAlign: 'center',
      align: 'left',
      minWidth: 170,
      renderCell: (params) => {
        if (params.row?.defaultCampaign) {
          return (
            <Box
              sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            >
              <DefaultCampaignChip />
            </Box>
          )
        }
        return (
          <div>
            <Typography sx={{ paddingTop: '8px' }}>
              {formatDateAndTime(params.row.initialDate)} -
            </Typography>
            <Typography sx={{ paddingBottom: '8px' }}>
              {' '}
              {formatDateAndTime(params.row.finalDate)}
            </Typography>
          </div>
        )
      },
    },
    {
      field: 'groupListName',
      headerName: t('campaign.table.header.groups'),
      width: 70,
      editable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: ({ row: { groups } }) => groups?.length,
      sortComparator: ArrToNumberSort,
    },
    {
      field: 'segmentedActive',
      headerName: t('campaign.table.header.segmentation'),
      width: 110,
      type: 'boolean',
      editable: false,
      renderCell: ({ row }) => {
        return (
          <>
            {row.segmentedActive ? (
              <Typography>
                {t('campaign.table.header.segmentation.label.yes')}
              </Typography>
            ) : (
              <Typography>
                {t('campaign.table.header.segmentation.label.no')}
              </Typography>
            )}
          </>
        )
      },
    },
  ]

  return (
    <>
      {showBlockModal && (
        <ModalBlockEditCampaing
          open={true}
          title={t('modal.title.block.edit.campain')}
          onHandleClose={() => setShowBlockModal(false)}
          onClick={() => setShowBlockModal(false)}
        />
      )}
      <Table
        columns={columns}
        getData={getData}
        key={tableKey}
        handleDelete={handleDelete}
        titleKey="name"
        goTo={goToEditCampaign}
        defaultPageSize={15}
        model="campaign"
        DetailComponent={CreateCampaign}
        size="16px"
        permissionUpdate={!permissions?.campaign?.includes('campaign:update')}
        permissionDelete={!permissions?.campaign?.includes('campaign:delete')}
        permissionView={!permissions?.campaign?.includes('campaign:view')}
        canShowRecycleButtons={true}
        permissionViewRecycle={permissions?.campaign?.includes(
          'campaign:recycle:view'
        )}
        permissionUpdateRecycle={permissions?.campaign?.includes(
          'campaign:recycle:update'
        )}
        handleRecycle={handleRecycle}
        canShowUnarchiveButtons={true}
        permissionViewUnarchive={permissions?.campaign?.includes(
          'campaign:archive:view'
        )}
        permissionUpdateUnarchive={permissions?.campaign?.includes(
          'campaign:archive:update'
        )}
        handleUnarchive={handleUnarchive}
        renderExtraRowActions={renderExtraRowActions}
        renderExtraToolbarActions={renderExtraToolbarActions}
        onBeforeEdit={handleBeforeEdit}
      />
    </>
  )
}
